import { useState } from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { Card } from '@/components/Shared/Card';
import { Button } from '@/components/Shared/Button';
import { FileUpload } from '@/components/Shared/FileUpload';
import { useContractImportStore } from '@/store/useContractImportStore';
import { CONTRACT_MAX_FILE_MB, isContractFileTooLarge } from '../logic/fileLimits';

export function ContractUploadStep() {
  const { t } = useTranslation('contractImport');
  const { analyze, analyzing, error, clearError } = useContractImportStore();
  const [files, setFiles] = useState<File[]>([]);
  const tooLarge = files[0] ? isContractFileTooLarge(files[0]) : false;
  // The size texts name the limit via {{maxMb}}, also when the server sends contractImport.fileTooLarge.
  const limit = { maxMb: CONTRACT_MAX_FILE_MB };

  // A failed analyze belongs to the previous file — drop its message once another is picked.
  const selectFiles = (next: File[]) => {
    setFiles(next);
    clearError();
  };

  return (
    <Card>
      <div className="space-y-4">
        <p className="text-[13px] text-slate-600">{t('upload.intro')}</p>

        <FileUpload
          label={t('upload.fileLabel')}
          accept=".pdf,application/pdf"
          files={files}
          onChange={selectFiles}
          helperText={t('upload.fileHint', limit)}
          error={tooLarge ? t('errors:contractImport.fileTooLarge', limit) : undefined}
          // why: an analyze in flight finishes for the file it started with; switching files
          // meanwhile would show one file and continue the wizard with the other.
          disabled={analyzing}
        />

        <div className="flex items-start gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-[12px] text-slate-600">
          <Clock className="w-4 h-4 shrink-0 mt-px text-slate-400" strokeWidth={2} />
          <span>{t('upload.longRunningHint')}</span>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-px" strokeWidth={2} />
            <span>{t(error, limit)}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button
            onClick={() => files[0] && analyze(files[0])}
            disabled={!files[0] || tooLarge}
            loading={analyzing}
          >
            {analyzing ? t('upload.analyzing') : t('actions.analyze')}
          </Button>
        </div>
      </div>
    </Card>
  );
}

export default ContractUploadStep;
